import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled, { useTheme } from 'styled-components';

// Container for the entire page
const Container = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

// Left side with logo
const LogoSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30%;
  background-color: #1A1625;
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    padding-top: 60px;
    padding-bottom: 30px;
  }
`;

// Logo container
const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 60px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

// Logo text
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CreatorText = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 500;
  margin-top: 8px;
`;

// Right side with content
const ContentSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 70%;
  padding: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  }
`;

// Card container
const InvitationCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

// Back button
const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  align-self: flex-start;
`;

// Title
const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

// Subtitle
const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Enterprise grid
const EnterpriseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Enterprise card
const EnterpriseCard = styled.div<{ $accepted?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 8px;
  background-color: #1A1625;
  border: 1px solid ${({ $accepted, theme }) => 
    $accepted ? theme.colors.primary : 'rgba(255, 255, 255, 0.1)'};
`;

// Enterprise icon
const EnterpriseIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

// Enterprise name
const EnterpriseName = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  text-align: center;
`;

// Action button
const ActionButton = styled.button<{ $accepted?: boolean }>`
  width: 100%;
  padding: 8px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background-color: ${({ $accepted }) => $accepted ? 'transparent' : '#9333EA'};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Social connection row
const SocialRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: #1A1625;
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

// Social icon
const SocialIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

// Social info
const SocialInfo = styled.div`
  flex: 1;
  margin-left: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
`;

// Connect button
const ConnectButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background-color: #1E1E1E;
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
`;

// Next button
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NextButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing.xl};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #7928CA;
  }
  
  &:disabled {
    background-color: #4B5563;
    cursor: not-allowed;
  }
`;

// Complete button
const CompleteButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: #9333EA;
  color: white;
  border: none;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.action.hover};
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
`;

// Verification overlay
const VerificationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

// Verification popup
const VerificationPopup = styled.div`
  background-color: #1E1E1E;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
`;

// Check icon container
const CheckIcon = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: transparent;
`;

// Verification title
const VerificationTitle = styled.h2`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: center;
`;

// Verification subtitle
const VerificationSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: 24px;
  text-align: center;
`;

// Let's Go button
const LetsGoButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: #9333EA;
  color: white;
  border: none;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.action.hover};
  }
`;

// Main component
const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [acceptedEnterprises, setAcceptedEnterprises] = useState({
    kayak: true,
    mindmatrix: true
  });
  
  const handleBack = () => {
    navigate('/invitation');
  };
  
  const toggleAccept = (enterprise: 'kayak' | 'mindmatrix') => {
    setAcceptedEnterprises(prev => ({
      ...prev,
      [enterprise]: !prev[enterprise]
    }));
  };
  
  const handleConnect = (platform: string) => {
    // TODO: Implement social connection logic
    console.log(`Connecting to ${platform}`);
  };
  
  const handleComplete = () => {
    setLoading(true);
    
    // TODO: Implement invitation acceptance logic
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setShowVerificationPopup(true);
    }, 1500);
  };
  
  const handleLetsGo = () => {
    navigate('/complete-profile');
  };
  
  return (
    <Container>
      {showVerificationPopup && (
        <VerificationOverlay>
          <VerificationPopup>
            <CheckIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="6" fill="#22C55E" />
                <path d="M7 12L10 15L17 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </CheckIcon>
            <VerificationTitle>Your account is verified!</VerificationTitle>
            <VerificationSubtitle>You're invitation was successfully linked</VerificationSubtitle>
            <LetsGoButton onClick={handleLetsGo}>Let's Go</LetsGoButton>
          </VerificationPopup>
        </VerificationOverlay>
      )}
      
      <LogoSide>
        <Logo>
          <img src="/creator-logo.svg" alt="BlinkLink Creator" />
        </Logo>
      </LogoSide>
      
      <ContentSide>
        <InvitationCard>
          <BackButton onClick={handleBack}>
            ← Back
          </BackButton>
          
          <Title>Accept Invitation?</Title>
          <Subtitle>Link pre-approvals to your account</Subtitle>
          
          <EnterpriseGrid>
            <EnterpriseCard $accepted={acceptedEnterprises.kayak}>
              <EnterpriseIcon>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="48" height="48" fill="#FF6B00"/>
                  <text x="24" y="30" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold">K</text>
                </svg>
              </EnterpriseIcon>
              <EnterpriseName>Kayak</EnterpriseName>
              <ActionButton 
                $accepted={acceptedEnterprises.kayak} 
                onClick={() => toggleAccept('kayak')}
              >
                {acceptedEnterprises.kayak ? 'Accepted' : 'Accept'}
              </ActionButton>
            </EnterpriseCard>
            
            <EnterpriseCard $accepted={acceptedEnterprises.mindmatrix}>
              <EnterpriseIcon>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="48" height="48" fill="#0066FF"/>
                  <text x="24" y="30" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold">M</text>
                </svg>
              </EnterpriseIcon>
              <EnterpriseName>Mindmatrix</EnterpriseName>
              <ActionButton 
                $accepted={acceptedEnterprises.mindmatrix} 
                onClick={() => toggleAccept('mindmatrix')}
              >
                {acceptedEnterprises.mindmatrix ? 'Accepted' : 'Accept'}
              </ActionButton>
            </EnterpriseCard>
          </EnterpriseGrid>
          
          <SocialRow>
            <SocialIcon>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" fill="#E1306C"/>
                <text x="16" y="20" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">IG</text>
              </svg>
            </SocialIcon>
            <SocialInfo>Instagram</SocialInfo>
            <ConnectButton onClick={() => handleConnect('instagram')}>
              Connect
            </ConnectButton>
          </SocialRow>
          
          <SocialRow>
            <SocialIcon>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" fill="#0077B5"/>
                <text x="16" y="20" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">LI</text>
              </svg>
            </SocialIcon>
            <SocialInfo>LinkedIn</SocialInfo>
            <ConnectButton onClick={() => handleConnect('linkedin')}>
              Connect
            </ConnectButton>
          </SocialRow>
          
          <CompleteButton onClick={handleComplete} disabled={loading}>
            {loading ? 'Processing...' : 'Complete Sign Up'}
          </CompleteButton>
        </InvitationCard>
      </ContentSide>
    </Container>
  );
};

export default AcceptInvitation;
